/**
 * Single-token fuzzy correction policy (plan §6.2).
 *
 * @remarks
 * Candidates come ONLY from the caller-built pool (compiled vocabulary + live
 * catalog tokens) — the parser never invents words. Length thresholds gate
 * how much correction a token may receive (defaults: under 4 characters none,
 * 4–6 distance ≤ 1, 7+ distance ≤ 2; caller-tunable via
 * `ParserOptions.fuzzyThresholds`). Exact and alias matches outrank fuzzy
 * absolutely — this module is only ever consulted for words the parser does
 * not recognize at all.
 *
 * Near-ties never silently pick: when two candidate words sit at the same
 * minimal distance, the outcome is a `near_tie` carrying every tied target
 * (in the pool's sorted order — deterministic), and the orchestrator decides
 * whether that becomes a clarification (all-catalog targets) or an
 * `unrecognized` with `nearMissSuggestions` (plan §6.2).
 *
 * @packageDocumentation
 */

import { type FuzzyLengthThreshold } from '../types.js';
import { boundedDamerauLevenshtein } from './damerau-levenshtein.js';

/** The §6.2 default tiers: <4 no correction, 4–6 distance ≤ 1, 7+ distance ≤ 2. */
export const DEFAULT_FUZZY_THRESHOLDS: readonly FuzzyLengthThreshold[] = [
	{ maxEditDistance: 1, minTokenLength: 4 },
	{ maxEditDistance: 2, minTokenLength: 7 },
];

/** The outcome of proposing a correction for one unknown word. */
export type TokenCorrectionProposal =
	| { readonly correctedText: string; readonly editDistance: number; readonly type: 'corrected' }
	| { readonly editDistance: number; readonly targetWords: readonly string[]; readonly type: 'near_tie' }
	| { readonly type: 'no_correction' };

/**
 * Propose a correction for one unknown word against the candidate pool.
 *
 * @param wordText - The unrecognized folded word
 * @param candidateWords - The pool, sorted and deduplicated (tie output order inherits it)
 * @param thresholds - The active length tiers (see {@link DEFAULT_FUZZY_THRESHOLDS})
 * @returns A unique correction, a near-tie naming every tied target, or nothing
 */
export const correctToken = (
	wordText: string,
	candidateWords: readonly string[],
	thresholds: readonly FuzzyLengthThreshold[] = DEFAULT_FUZZY_THRESHOLDS,
): TokenCorrectionProposal => {
	const allowedDistance = maxDistanceForLength(wordText.length, thresholds);

	if (allowedDistance === 0) {
		return { type: 'no_correction' };
	}

	let bestDistance = Number.POSITIVE_INFINITY;
	let bestTargets: string[] = [];

	for (const candidateWord of candidateWords) {
		const distance = boundedDamerauLevenshtein(wordText, candidateWord, allowedDistance);

		if (distance > allowedDistance) {
			continue;
		}

		if (distance < bestDistance) {
			bestDistance = distance;
			bestTargets = [candidateWord];
			continue;
		}

		if (distance === bestDistance) {
			bestTargets.push(candidateWord);
		}
	}

	const [primaryTarget, ...tiedTargets] = bestTargets;

	if (primaryTarget === undefined) {
		return { type: 'no_correction' };
	}

	if (tiedTargets.length === 0) {
		return { correctedText: primaryTarget, editDistance: bestDistance, type: 'corrected' };
	}

	return { editDistance: bestDistance, targetWords: bestTargets, type: 'near_tie' };
};

/**
 * The largest edit distance a token of this length may receive.
 *
 * @param tokenLength - The unknown word's character count
 * @param thresholds - The active length tiers
 * @returns The allowed distance (0 = no correction at this length)
 */
export const maxDistanceForLength = (tokenLength: number, thresholds: readonly FuzzyLengthThreshold[]): number =>
	thresholds.reduce(
		(allowedDistance, threshold) =>
			tokenLength >= threshold.minTokenLength ? Math.max(allowedDistance, threshold.maxEditDistance) : allowedDistance,
		0,
	);
